import React from 'react';

export type QueryFetchStatus = {
  isFetching: boolean;
  isFetched: boolean;
};

/**
 * Tracks whether a Firestore (or any async) query has resolved.
 *
 * Convention: the query hook returns `undefined` while in-flight,
 * and an array (empty or populated) once the first snapshot arrives.
 *
 * @param queryResult - The raw result from the query hook (T[] | undefined)
 * @returns { isFetching, isFetched }
 */
export function useQueryFetchStatus<T>(queryResult: T[] | undefined): QueryFetchStatus {
  // Latch: once true it never resets — prevents flicker on re-renders
  const hasFetched = React.useRef(false);
  const [isFetched, setIsFetched] = React.useState(false);

  React.useEffect(() => {
    if (queryResult !== undefined && !hasFetched.current) {
      hasFetched.current = true; // sync guard — prevents double setState
      setIsFetched(true);
    }
  }, [queryResult]);

  return {
    isFetching: !isFetched,
    isFetched,
  };
}
